<script>
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import Button from "lib/components/Button.svelte";
  import AccordionPagination from "lib/components/accordionPagination.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  export let catalog = [];
  export let framework = { L1: [], L2: [], L3: [] };
  export let loading = false;
  export let projectId = "";
  export let diagramState = { activities: [], gateways: 0, hasEnd: false };
  export let recordTypes = [];
  export let drawings = [];

  const dispatch = createEventDispatcher();
  const pageSize = 5;
  const frameworkLevels = ["L1", "L2", "L3"];
  let stage = "start";
  let frameworkLevelIndex = 0;
  let lastFrameworkLevelIndex = 0;
  let selectedFramework = null;
  let taskGrouping = "Team";
  let selectedTaskIds = [];
  let search = "";
  let page = 1;
  let input = "";
  let asking = false;
  let language = "English";
  let information = {
    avgTime: true,
    throughputTime: false,
    frequency: false,
    databases: true,
    files: true,
    addSubprocess: false,
  };
  let artifactMode = "balanced";
  // Ausgabeformat und Ausrichtung: letzter Schritt des gefuehrten Ablaufs.
  let exportFormat = "standard";
  let orientation = "horizontal";
  let generateTexts = false;

  /** Sofort melden, damit die Vorschau der Auswahl direkt folgt. */
  function chooseOutput(format, direction) {
    exportFormat = format;
    orientation = direction;
    dispatch("output", { exportFormat, orientation, generateTexts });
  }
  let systemMinFrequency = 20;
  let fileMinFrequency = 20;
  let includedTaskIds = [];
  let pendingTasks = [];
  let requestedMode = "";
  let gatewayQuestion = "What determines the path?";
  let pendingGatewayType = "";
  let pathDescriptions = [];
  let customTeam = "";
  let manualActivities = [];
  let manualModalOpen = false;
  let manualSaving = false;
  let manualError = "";
  let resetDangerModalOpen = false;
  let manualForm = {
    name: "",
    team: "",
    avgMinutes: "",
    avgSeconds: "",
    frequency: "",
    systems: "",
    files: "",
  };
  let lastSuggestedActivity = "";
  let drawingSearch = "";
  let messages = [
    {
      role: "assistant",
      text: "Would you like to create a new BPMN model, continue a saved model, or start from an object type?",
    },
  ];
  const fallbackTeamOptions = [
    "Project Backoffice",
    "Finance",
    "Field Technicians",
  ];

  $: frameworkLevel = frameworkLevels[frameworkLevelIndex] || "L1";
  $: frameworkOptions = framework[frameworkLevel] || [];
  $: if (frameworkLevelIndex !== lastFrameworkLevelIndex) {
    lastFrameworkLevelIndex = frameworkLevelIndex;
    page = 1;
  }
  $: availableCatalog = [...catalog, ...manualActivities];
  $: matchingTasks = availableCatalog
    .filter(matchesSelectedFramework)
    .filter((task) =>
      String(task.name || "")
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    )
    .sort(
      (a, b) =>
        taskGroup(a).localeCompare(taskGroup(b)) ||
        a.name.localeCompare(b.name),
    );
  $: pageCount = Math.max(1, Math.ceil(matchingTasks.length / pageSize));
  $: visibleTasks = matchingTasks.slice((page - 1) * pageSize, page * pageSize);
  $: matchingDrawings = drawings.filter((drawing) =>
    drawing.name.toLowerCase().includes(drawingSearch.toLowerCase()),
  );
  $: existingDrawingPageCount = Math.max(
    1,
    Math.ceil(matchingDrawings.length / pageSize),
  );
  $: frameworkPageCount = Math.max(
    1,
    Math.ceil(frameworkOptions.length / pageSize),
  );
  $: activePageCount =
    stage === "existing"
      ? existingDrawingPageCount
      : stage === "recordTypeStart"
        ? Math.max(1, Math.ceil(recordTypes.length / pageSize))
        : stage === "framework"
          ? frameworkPageCount
          : pageCount;
  $: if (page > activePageCount) page = activePageCount;
  $: teamOptions = (() => {
    const catalogTeams = [
      ...new Set(
        availableCatalog.flatMap((task) => task.teams || []).filter(Boolean),
      ),
    ].sort();
    return [...new Set([...fallbackTeamOptions, ...catalogTeams])].sort();
  })();
  $: effectiveSystemMin =
    artifactMode === "simple"
      ? 10
      : artifactMode === "balanced"
        ? 20
        : systemMinFrequency;
  $: effectiveFileMin =
    artifactMode === "simple"
      ? 10
      : artifactMode === "balanced"
        ? 20
        : fileMinFrequency;
  $: currentPhase = [
    "start",
    "framework",
    "existing",
    "recordTypeStart",
    "recordTypeLoading",
  ].includes(stage)
    ? 1
    : [
          "activities",
          "next",
          "branching",
          "conditions",
          "gatewayDetails",
        ].includes(stage)
      ? 2
      : ["information", "recordType"].includes(stage)
        ? 3
        : 4;
  $: currentPhaseLabel =
    ["Start", "Activities", "Details", "Review"][currentPhase - 1] || "Start";
  $: moderatorText =
    [...messages].reverse().find((message) => message.role === "assistant")
      ?.text || "";

  function matchesSelectedFramework(task) {
    if (task.manual) return true;
    if (!selectedFramework) return true;
    return (
      String(task.groups?.[frameworkLevel]?.id || "") ===
      String(selectedFramework.id)
    );
  }

  function taskGroup(task) {
    if (taskGrouping === "Team") return task.teams?.[0] || "Other Teams";
    return task.groups?.[taskGrouping]?.name || `Other ${taskGrouping}`;
  }

  function addMessage(role, text) {
    messages = [...messages, { role, text }];
  }

  function chooseNewProcess() {
    addMessage("user", "Create a new model");
    addMessage(
      "assistant",
      "Where should this BPMN model start in your framework?",
    );
    page = 1;
    stage = "framework";
  }

  function chooseExistingDrawing(drawing) {
    const xml = new DOMParser().parseFromString(drawing.xml, "application/xml");
    const names = new Set(
      [...xml.querySelectorAll("[name]")]
        .filter((node) => ["task", "subProcess"].includes(node.localName))
        .map((node) => node.getAttribute("name"))
        .filter(Boolean),
    );
    includedTaskIds = availableCatalog
      .filter((task) => names.has(task.name))
      .map((task) => task.id);
    addMessage("user", `Continue: ${drawing.name}`);
    addMessage(
      "assistant",
      "I've loaded the saved model. What should happen next?",
    );
    dispatch("loadDrawing", { drawing });
    stage = "next";
  }

  function chooseRecordType(recordType) {
    includedTaskIds = [];
    addMessage("user", `Object type: ${recordType.name}`);
    addMessage(
      "assistant",
      "I'm generating the recorded object flow as your starting model.",
    );
    stage = "recordTypeLoading";
    dispatch("recordType", {
      recordType,
      complete: (success) => {
        if (success) {
          addMessage(
            "assistant",
            "The starting model is ready. What should happen next?",
          );
          stage = "next";
        } else stage = "recordTypeStart";
      },
    });
  }

  function chooseFramework(item) {
    selectedFramework = item;
    selectedTaskIds = [];
    search = "";
    page = 1;
    addMessage("user", `${frameworkLevel}: ${item.name}`);
    addMessage(
      "assistant",
      `Great. The start event is ready for “${item.name}”. Which L4 activities happen directly after the start? You can select one or several.`,
    );
    stage = "activities";
    dispatch("process", {
      name: item.name,
      level: frameworkLevel,
      id: item.id,
    });
  }

  // Verzweigungen brauchen zwei Aktivitaeten - sonst gibt es nichts zu
  // verzweigen. Der Knopf bleibt bis dahin gesperrt: vorher war er ab einer
  // Auswahl anklickbar und wiederholte nur die Aufforderung von oben, was
  // aussah, als passiere nichts.
  $: requiredTaskCount = requestedMode ? 2 : 1;

  function addSelectedActivities() {
    const tasks = availableCatalog.filter((task) =>
      selectedTaskIds.includes(task.id),
    );
    if (!tasks.length) return;
    if (requestedMode && tasks.length < 2) {
      addMessage(
        "assistant",
        `Please select at least two activities for the ${requestedMode === "conditional" ? "if/else" : "parallel"} paths.`,
      );
      return;
    }
    includedTaskIds = [
      ...new Set([...includedTaskIds, ...tasks.map((task) => task.id)]),
    ];
    addMessage("user", tasks.map((task) => task.name).join(", "));
    selectedTaskIds = [];
    if (
      tasks.length > 1 &&
      (requestedMode === "conditional" || !requestedMode)
    ) {
      pendingTasks = tasks;
      if (requestedMode === "conditional") prepareConditions(tasks);
      else {
        addMessage(
          "assistant",
          "How are these activities related: sequential, parallel, or conditional (if/else)?",
        );
        stage = "branching";
      }
      return;
    }
    addActivities(
      tasks,
      requestedMode || "sequence",
      requestedMode ? { question: gatewayQuestion.trim() } : {},
    );
  }

  function addActivities(tasks, mode, options = {}) {
    addMessage(
      "assistant",
      "I've added that section to the live model. What should happen next?",
    );
    dispatch("activities", { tasks, mode, ...options });
    pendingTasks = [];
    requestedMode = "";
    pendingGatewayType = "";
    gatewayQuestion = "";
    stage = "next";
  }

  function openManualActivityModal() {
    manualForm = {
      name: "",
      team: teamOptions[0] || "",
      avgMinutes: "",
      avgSeconds: "",
      frequency: "",
      systems: "",
      files: "",
    };
    manualError = "";
    manualModalOpen = true;
  }

  async function saveManualActivity() {
    if (!manualForm.name.trim() || manualSaving) return;
    manualSaving = true;
    manualError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/bpmn-draw/manual-activities/${encodeURIComponent(projectId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: manualForm.name.trim(),
            teams: manualForm.team ? [manualForm.team] : [],
            avg_time_seconds:
              Number(manualForm.avgMinutes || 0) * 60 +
              Math.min(59, Number(manualForm.avgSeconds || 0)),
            monthly_frequency: Number(manualForm.frequency || 0),
            databases: manualForm.systems
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
            files: manualForm.files
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
          }),
        },
      );
      const saved = await response.json();
      if (!response.ok)
        throw new Error(saved.message || "Could not save activity.");
      manualActivities = [
        ...manualActivities.filter((item) => item.id !== saved.id),
        saved,
      ];
      selectedTaskIds = [...new Set([...selectedTaskIds, saved.id])];
      customTeam = saved.teams?.[0] || customTeam;
      manualModalOpen = false;
      addMessage(
        "assistant",
        `“${saved.name}” was saved as a manual activity and selected.`,
      );
    } catch (error) {
      manualError = error.message || "Could not save activity.";
    } finally {
      manualSaving = false;
    }
  }

  function backFromActivities() {
    selectedTaskIds = [];
    requestedMode = "";
    if (diagramState.activities?.length) {
      stage = "next";
    } else {
      selectedFramework = null;
      stage = "framework";
      dispatch("reset");
    }
  }

  function prepareConditions(tasks) {
    pendingTasks = tasks;
    gatewayQuestion = gatewayQuestion.trim() || "What determines the path?";
    pathDescriptions = tasks.map((task, index) =>
      index === tasks.length - 1 ? "Else" : `If ${task.name}`,
    );
    addMessage(
      "assistant",
      "What is the XOR decision question, and what condition leads to each path?",
    );
    stage = "conditions";
  }

  function confirmConditions() {
    addActivities(pendingTasks, "conditional", {
      question: gatewayQuestion.trim(),
      conditions: pathDescriptions.map((text) => text.trim()),
    });
  }

  function chooseNext(next) {
    if (next === "activity") {
      addMessage("user", "Add more activities");
      addMessage(
        "assistant",
        "Select the next L4 activities. I will append them to the current model state.",
      );
      stage = "activities";
      return;
    }
    if (next === "finish") {
      addMessage("user", "Finish the process flow");
      addMessage(
        "assistant",
        "The end event is in place. Give your BPMN diagram the final shape and add additional information to your needs.",
      );
      dispatch("finish");
      stage = "information";
      return;
    }
    const gatewayTypes = {
      exclusive: "bpmn:ExclusiveGateway",
      parallel: "bpmn:ParallelGateway",
      inclusive: "bpmn:InclusiveGateway",
    };
    addMessage("user", `Add ${next} gateway`);
    pendingGatewayType = next;
    gatewayQuestion = "";
    addMessage("assistant", "How should I label this gateway or decision?");
    stage = "gatewayDetails";
  }

  function confirmGatewayDetails() {
    const gatewayTypes = {
      exclusive: "bpmn:ExclusiveGateway",
      parallel: "bpmn:ParallelGateway",
      inclusive: "bpmn:InclusiveGateway",
    };
    if (
      pendingGatewayType === "exclusive" ||
      pendingGatewayType === "parallel"
    ) {
      requestedMode =
        pendingGatewayType === "exclusive" ? "conditional" : "parallel";
      addMessage(
        "assistant",
        `Now select at least two activities for the ${pendingGatewayType} paths.`,
      );
      stage = "activities";
    } else {
      dispatch("element", {
        type: gatewayTypes[pendingGatewayType],
        name: gatewayQuestion.trim(),
      });
      addMessage(
        "assistant",
        "The labelled gateway is in place. Select what follows it.",
      );
      stage = "activities";
    }
  }

  /** Die Angaben, die das Modell zum Anreichern braucht. */
  function informationDetail(recordType = null) {
    return {
      flags: information,
      taskIds: includedTaskIds,
      manualTasks: manualActivities.filter((task) =>
        includedTaskIds.includes(task.id),
      ),
      recordType: recordType || recordTypes[0] || null,
      thresholds: { systems: effectiveSystemMin, files: effectiveFileMin },
      exportFormat,
      orientation,
      generateTexts,
    };
  }

  // Haken setzen und nichts passiert - bis man ganz unten "Apply and complete"
  // drueckt. Deshalb zeigt die Vorschau die Angaben jetzt sofort. Kurz
  // gebremst, weil je Aufgabe Kennzahlen nachgeladen werden.
  let previewTimer = null;
  function previewInformation() {
    if (stage !== "information") return;
    clearTimeout(previewTimer);
    previewTimer = setTimeout(
      () => dispatch("information-preview", informationDetail()),
      400,
    );
  }
  $: if (stage === "information" && information) previewInformation();
  onDestroy(() => clearTimeout(previewTimer));

  function applyInformation(recordType = null) {
    if (information.throughputTime && recordTypes.length > 1 && !recordType) {
      addMessage(
        "assistant",
        "Several object types are available. Which one should provide the average throughput times?",
      );
      stage = "recordType";
      return;
    }
    const selected = Object.entries(information)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key);
    addMessage(
      "user",
      selected.length ? selected.join(", ") : "No additional information",
    );
    addMessage(
      "assistant",
      "Done. Your BPMN model is complete. You can continue editing it manually or export it now.",
    );
    dispatch("information", informationDetail(recordType));
    stage = "complete";
  }

  function reset() {
    stage = "start";
    selectedFramework = null;
    selectedTaskIds = [];
    includedTaskIds = [];
    pendingTasks = [];
    requestedMode = "";
    pendingGatewayType = "";
    search = "";
    page = 1;
    drawingSearch = "";
    messages = [
      {
        role: "assistant",
        text: "Would you like to create a new BPMN model, continue a saved model, or start from an object type?",
      },
    ];
    dispatch("reset");
  }

  async function ask() {
    const question = input.trim();
    if (!question || asking) return;
    const history = messages;
    addMessage("user", question);
    input = "";
    if (handleDeferredRequest(question) || (await handlePhaseCommand(question)))
      return;
    asking = true;
    try {
      const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          history,
          context: {
            page: "BPMN Draw Assistant",
            projectId,
            responseLanguage: language,
            currentStep: stage,
            selectedFramework,
            diagram: diagramState,
            instruction:
              "Help the user create the BPMN model. Refer to the guided choices and current live diagram state. Be concise.",
          },
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status === "error")
        throw new Error(result.message || "Pax AI request failed");
      addMessage("assistant", result.answer);
      await handleChatAction(question, result.answer);
    } catch (error) {
      addMessage(
        "assistant",
        `I couldn't answer that right now: ${error.message}`,
      );
    } finally {
      asking = false;
    }
  }

  function localized(english, german) {
    return language === "German" ? german : english;
  }

  function handleDeferredRequest(question) {
    const asksForInformation =
      /(average|avg|time|zeit|throughput|durchlauf|frequency|häufig|system|database|datei|file|document|subprocess|teilprozess)/i.test(
        question,
      );
    const asksForExport = /(export|download|review|prüf|herunterladen)/i.test(
      question,
    );
    const asksForModeling =
      /(activity|aktivität|task|gateway|xor|parallel|branch|verzweigung|loop|schleife|hinzuf|einfüg|übernehm)/i.test(
        question,
      );
    if (asksForInformation && currentPhase < 3) {
      addMessage(
        "assistant",
        localized(
          "That belongs to phase 3, Information. Let's finish the process structure first; I will remind you there.",
          "Das gehört in Phase 3 „Informationen“. Lass uns zuerst die Prozessstruktur abschließen; ich greife die Anfrage dort wieder auf.",
        ),
      );
      return true;
    }
    if (asksForExport && currentPhase < 4) {
      addMessage(
        "assistant",
        localized(
          "Review and export are phase 4. I will offer this as soon as the model and its information are complete.",
          "Review und Export kommen in Phase 4. Ich biete das an, sobald Modell und Informationen vollständig sind.",
        ),
      );
      return true;
    }
    if (asksForModeling && currentPhase === 1) {
      addMessage(
        "assistant",
        localized(
          "Activities and gateways belong to phase 2. Please choose the process framework first.",
          "Aktivitäten und Gateways kommen in Phase 2. Bitte wähle zuerst den Prozess aus dem Framework.",
        ),
      );
      return true;
    }
    return false;
  }

  function extractRequestedActivity(question) {
    const quoted = question.match(/[„“”"']([^„“”"']{2,80})[„“”"']/)?.[1];
    if (quoted) return quoted;
    const patterns = [
      /(?:füge|fuege|add|insert|create)\s+(?:die\s+|the\s+)?(?:aktivität|activity|task)?\s*(.+?)\s+(?:hinzu|ein|to the model)$/i,
      /(?:aktivität|activity|task)\s+(?:namens|named\s+)?(.+?)(?:\s+(?:hinzufügen|hinzufuegen|einfügen|einfuegen|add|insert))?$/i,
      /(?:kannst du(?: mir)?\s+|please\s+)?(.+?)\s+(?:hinzufügen|hinzufuegen|einfügen|einfuegen|add|insert)$/i,
    ];
    return (
      patterns
        .map((pattern) => question.match(pattern)?.[1]?.trim())
        .find(Boolean) || ""
    );
  }

  async function addChatActivity(question, fallbackName = "") {
    const lower = question.toLowerCase();
    let task = availableCatalog.find((item) =>
      lower.includes(String(item.name).toLowerCase()),
    );
    let name = task?.name || extractRequestedActivity(question) || fallbackName;
    const referencesSuggestion = /\b(das|dies|it|that)\b/i.test(question);
    if (referencesSuggestion && lastSuggestedActivity)
      name = lastSuggestedActivity;
    else if (
      referencesSuggestion &&
      /^(das|dies|it|that)$/i.test(String(name || "").trim())
    )
      return false;
    name = String(name || "")
      .replace(/[.?!]$/, "")
      .trim();
    if (!name) return false;
    if (!task) {
      const fallback = {
        id: `manual-${Date.now()}`,
        name,
        teams: [customTeam || teamOptions[0] || "Other Teams"],
        databases: [],
        files: [],
        manual: true,
      };
      try {
        const response = await fetchWithSession(
          `/api/damprojects/bpmn-draw/manual-activities/${encodeURIComponent(projectId)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fallback),
          },
        );
        task = response.ok ? await response.json() : fallback;
      } catch {
        task = fallback;
      }
      manualActivities = [
        ...manualActivities.filter((item) => item.id !== task.id),
        task,
      ];
    }
    includedTaskIds = [...new Set([...includedTaskIds, task.id])];
    addActivities([task], "sequence");
    return true;
  }

  async function handlePhaseCommand(question) {
    const lower = question.toLowerCase();
    if (currentPhase === 1) {
      const frameworkItem = Object.entries(framework)
        .flatMap(([level, items]) => items.map((item) => ({ ...item, level })))
        .find((item) => lower.includes(String(item.name).toLowerCase()));
      if (frameworkItem) {
        frameworkLevelIndex = frameworkLevels.indexOf(frameworkItem.level);
        if (frameworkLevelIndex < 0) frameworkLevelIndex = 0;
        chooseFramework(frameworkItem);
        return true;
      }
    }
    if (
      currentPhase >= 2 &&
      /(add|insert|include|create|hinzuf|einfüg|übernehm|uebernehm|erstell|ins modell|modell aufnehmen)/i.test(
        question,
      ) &&
      (currentPhase === 2 ||
        /(activity|task|aktivität)/i.test(question) ||
        availableCatalog.some((task) =>
          lower.includes(String(task.name).toLowerCase()),
        ) ||
        /\b(das|dies|it|that)\b/i.test(question) ||
        (lastSuggestedActivity && /(ins modell|übernehm)/i.test(question)))
    ) {
      const mentionedTasks = availableCatalog.filter((task) =>
        lower.includes(String(task.name).toLowerCase()),
      );
      const mode = /(parallel)/i.test(question)
        ? "parallel"
        : /(xor|if.?else|conditional|verzweig)/i.test(question)
          ? "conditional"
          : "sequence";
      if (mentionedTasks.length) {
        includedTaskIds = [
          ...new Set([
            ...includedTaskIds,
            ...mentionedTasks.map((task) => task.id),
          ]),
        ];
        if (mode === "conditional" && mentionedTasks.length > 1)
          prepareConditions(mentionedTasks);
        else addActivities(mentionedTasks, mode);
        return true;
      }
      if (mode === "parallel" || mode === "conditional") {
        chooseNext(mode === "parallel" ? "parallel" : "exclusive");
        return true;
      }
      if (await addChatActivity(question, lastSuggestedActivity)) return true;
    }
    if (
      currentPhase === 3 &&
      /(add|include|enable|hinzuf|aktivier|übernehm)/i.test(question)
    ) {
      const updates = {};
      if (/(avg|average|bearbeitungszeit|activity time)/i.test(question))
        updates.avgTime = true;
      if (/(throughput|durchlauf)/i.test(question))
        updates.throughputTime = true;
      if (/(system|database|datenbank)/i.test(question))
        updates.databases = true;
      if (/(file|document|datei|dokument)/i.test(question))
        updates.files = true;
      if (/(frequency|häufig)/i.test(question)) updates.frequency = true;
      if (/(subprocess|teilprozess)/i.test(question))
        updates.addSubprocess = true;
      if (Object.keys(updates).length) {
        information = { ...information, ...updates };
        addMessage(
          "assistant",
          localized(
            "I selected those options for this phase. You can review the thresholds before applying them.",
            "Ich habe die Optionen für diese Phase ausgewählt. Du kannst vor dem Anwenden noch die Schwellenwerte prüfen.",
          ),
        );
        return true;
      }
    }
    if (
      currentPhase === 4 &&
      /(export|download|herunterladen)/i.test(question)
    ) {
      addMessage(
        "assistant",
        localized(
          "The completed model is being prepared for export.",
          "Das fertige Modell wird für den Export vorbereitet.",
        ),
      );
      dispatch("export");
      return true;
    }
    return false;
  }

  async function handleChatAction(question, answer) {
    const quotedSuggestion = String(answer || "").match(
      /[„“”\"']([^„“”\"']{2,80})[„“”\"']/,
    )?.[1];
    const quotedQuestion = String(question || "").match(
      /[„“”\"']([^„“”\"']{2,80})[„“”\"']/,
    )?.[1];
    if (quotedSuggestion)
      lastSuggestedActivity = quotedSuggestion.replace(/[.?!]$/, "");
    const addIntent =
      /(add|insert|include|create|hinzuf[uü]gen|einfügen|übernehmen|ins modell|modell aufnehmen)/i.test(
        question,
      ) ||
      /(i(?:'ll| will) (?:add|place|insert)|ich (?:füge|übernehme|platziere))/i.test(
        String(answer || ""),
      );
    if (!addIntent || !diagramState.started) return;
    const catalogTask = availableCatalog.find((task) =>
      String(question).toLowerCase().includes(String(task.name).toLowerCase()),
    );
    const namedActivity = question.match(
      /(?:activity|aktivität)\s+[„“\"']?(.+?)[„“\"']?(?:\s+(?:add|hinzufügen|einfügen))?[.!?]?$/i,
    )?.[1];
    const name =
      catalogTask?.name ||
      quotedQuestion ||
      namedActivity ||
      quotedSuggestion ||
      lastSuggestedActivity;
    if (!name) return;
    let task = catalogTask;
    if (!task) {
      const fallback = {
        id: `manual-${Date.now()}`,
        name: name.trim(),
        teams: [customTeam || teamOptions[0] || "Other Teams"],
        databases: [],
        files: [],
        manual: true,
      };
      try {
        const response = await fetchWithSession(
          `/api/damprojects/bpmn-draw/manual-activities/${encodeURIComponent(projectId)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fallback),
          },
        );
        task = response.ok ? await response.json() : fallback;
      } catch (error) {
        task = fallback;
      }
      manualActivities = [
        ...manualActivities.filter((item) => item.id !== task.id),
        task,
      ];
    }
    includedTaskIds = [...new Set([...includedTaskIds, task.id])];
    addActivities([task], "sequence");
    addMessage("assistant", `Added “${task.name}” to the live model.`);
  }

  onMount(async () => {
    if (!projectId) return;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/bpmn-draw/manual-activities/${encodeURIComponent(projectId)}`,
      );
      const data = await response.json();
      if (response.ok) manualActivities = Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Failed to load manual BPMN activities:", error);
    }
  });
</script>

<section class="draw_assistant">
  <header>
    <div>
      <strong>Pax AI</strong><span
        ><b>Phase: {currentPhaseLabel}</b> · {diagramState.activities?.length ||
          0} activities · {diagramState.gateways || 0} gateways</span
      >
    </div>
    <div class="header_actions">
      <button
        type="button"
        title="Reset model assistant"
        aria-label="Reset model assistant"
        on:click={() => (resetDangerModalOpen = true)}
        ><img src="/img/assistant/icons/reset.svg" alt="" /></button
      >
    </div>
  </header>

  <div class="assistant_scroll">
    <div class="moderator_card">
      <img src="/img/assistant/paxbot_portrait.png" alt="Pax AI" />
      <div>
        <strong>Pax AI</strong>
        <p>{asking ? "Thinking..." : moderatorText}</p>
      </div>
    </div>
    <div class="suggestions">
      {#if stage === "start"}
        <div class="start_choices">
          <Button
            variant="secondary"
            text="Create new model"
            width="100%"
            on:click={chooseNewProcess}
          /><Button
            variant="secondary"
            text="Continue saved model"
            width="100%"
            disabled={!drawings.length}
            on:click={() => {
              drawingSearch = "";
              page = 1;
              stage = "existing";
            }}
          /><Button
            variant="secondary"
            text="Start from object type"
            width="100%"
            disabled={!recordTypes.length}
            on:click={() => {
              page = 1;
              stage = "recordTypeStart";
            }}
          />
        </div>
      {:else if stage === "existing"}
        <input
          class="drawing_search"
          type="search"
          bind:value={drawingSearch}
          on:input={() => (page = 1)}
          placeholder="Search saved models"
        />
        <div class="saved_model_table">
          <div class="saved_model_table_header">
            <span>Model</span><span>Updated</span>
          </div>
          {#each matchingDrawings.slice((page - 1) * pageSize, page * pageSize) as drawing}
            <button
              type="button"
              class="saved_model_table_row"
              on:click={() => chooseExistingDrawing(drawing)}
            >
              <span title={drawing.name}>{drawing.name}</span>
              <span
                >{new Date(
                  drawing.updatedAt || drawing.createdAt,
                ).toLocaleDateString()}</span
              >
            </button>
          {:else}
            <div class="saved_model_table_empty">
              No saved BPMN models found.
            </div>
          {/each}
        </div>
        <div class="task_footer">
          <div class="pagination">
            <button
              type="button"
              disabled={page <= 1}
              on:click={() => (page -= 1)}>&lt;</button
            ><span>{page} / {existingDrawingPageCount}</span><button
              type="button"
              disabled={page >= existingDrawingPageCount}
              on:click={() => (page += 1)}>&gt;</button
            >
          </div>
          <Button
            variant="secondary"
            text="Back"
            on:click={() => (stage = "start")}
          />
        </div>
      {:else if stage === "recordTypeStart"}
        <div class="suggestion_list">
          {#each recordTypes.slice((page - 1) * pageSize, page * pageSize) as recordType}<button
              type="button"
              on:click={() => chooseRecordType(recordType)}
              >{recordType.name}</button
            >{/each}{#if !recordTypes.length}<p>No object types found.</p>{/if}
        </div>
        <div class="task_footer">
          <div class="pagination">
            <button
              type="button"
              disabled={page <= 1}
              on:click={() => (page -= 1)}>&lt;</button
            ><span
              >{page} / {Math.max(
                1,
                Math.ceil(recordTypes.length / pageSize),
              )}</span
            ><button
              type="button"
              disabled={page >= Math.ceil(recordTypes.length / pageSize)}
              on:click={() => (page += 1)}>&gt;</button
            >
          </div>
          <button type="button" on:click={() => (stage = "start")}>Back</button>
        </div>
      {:else if stage === "recordTypeLoading"}
        <div class="complete">
          <strong>Generating BPMN model...</strong><span
            >The recorded object flow is being prepared.</span
          >
        </div>
      {:else if stage === "framework"}
        <div class="framework_switcher">
          <ContentSwitcher
            options={frameworkLevels}
            bind:selected={frameworkLevelIndex}
          />
        </div>
        <div class="suggestion_list framework_list">
          {#each frameworkOptions.slice((page - 1) * pageSize, page * pageSize) as item}<button
              type="button"
              class="framework_option"
              title={item.name}
              on:click={() => chooseFramework(item)}>{item.name}</button
            >{/each}
          {#if !frameworkOptions.length}<p>
              {loading
                ? "Loading process framework..."
                : "No framework entries found."}
            </p>{/if}
        </div>
        {#if frameworkOptions.length}
          <AccordionPagination
            total={frameworkOptions.length}
            bind:currentpage={page}
            itemsPerPage={pageSize}
            disablePrev={page === 1}
            disableNext={page >= frameworkPageCount}
          />
        {/if}
      {:else if stage === "activities"}
        <div class="task_tools">
          <input
            type="search"
            bind:value={search}
            placeholder="Search L4 tasks"
          /><select bind:value={taskGrouping} on:change={() => (page = 1)}
            >{#each ["Team", "L1", "L2", "L3"] as grouping}<option
                value={grouping}>Group by {grouping}</option
              >{/each}</select
          ><button
            type="button"
            title="Create manual activity"
            aria-label="Create manual activity"
            on:click={openManualActivityModal}>+</button
          >
        </div>
        <div class="activity_table">
          <div class="activity_table_header">
            <span>Include</span><span>Activity</span><span>{taskGrouping}</span>
          </div>
          {#each visibleTasks as task}
            <label class="activity_table_row">
              <input
                type="checkbox"
                checked={selectedTaskIds.includes(task.id)}
                on:change={() =>
                  (selectedTaskIds = selectedTaskIds.includes(task.id)
                    ? selectedTaskIds.filter((id) => id !== task.id)
                    : [...selectedTaskIds, task.id])}
              />
              <span title={task.name}
                >{task.name}{#if task.manual}<small class="manual_badge"
                    >Manual</small
                  >{/if}</span
              >
              <span title={taskGroup(task)}>{taskGroup(task)}</span>
            </label>
          {:else}
            <div class="activity_table_empty">
              {loading ? "Loading L4 tasks..." : "No matching L4 tasks."}
            </div>
          {/each}
        </div>
        <div class="task_footer">
          <div class="pagination">
            <button
              type="button"
              disabled={page <= 1}
              on:click={() => (page -= 1)}>&lt;</button
            ><span>{page} / {pageCount}</span><button
              type="button"
              disabled={page >= pageCount}
              on:click={() => (page += 1)}>&gt;</button
            >
          </div>
          <span class="selection_count">
            {selectedTaskIds.length} selected{#if selectedTaskIds.length < requiredTaskCount}&nbsp;&middot;
              {requiredTaskCount} needed{/if}
          </span>
          <div class="task_footer_actions">
            <Button
              variant="secondary"
              text="Back"
              on:click={backFromActivities}
            />
            <Button
              variant="secondary"
              text="Add selected"
              disabled={selectedTaskIds.length < requiredTaskCount}
              on:click={addSelectedActivities}
            />
          </div>
        </div>
      {:else if stage === "next"}
        <div class="next_choices">
          <Button
            variant="secondary"
            text="Add activity"
            width="100%"
            on:click={() => chooseNext("activity")}
          /><Button
            variant="secondary"
            text="Add exclusive gateway"
            width="100%"
            on:click={() => chooseNext("exclusive")}
          /><Button
            variant="secondary"
            text="Add parallel gateway"
            width="100%"
            on:click={() => chooseNext("parallel")}
          /><Button
            variant="secondary"
            text="Add inclusive gateway"
            width="100%"
            on:click={() => chooseNext("inclusive")}
          /><Button
            variant="primary"
            text="Finish process flow"
            width="100%"
            on:click={() => chooseNext("finish")}
          />
        </div>
      {:else if stage === "gatewayDetails"}
        <div class="condition_editor">
          <input
            type="text"
            bind:value={gatewayQuestion}
            placeholder={pendingGatewayType === "exclusive"
              ? "e.g. Is the request complete?"
              : "e.g. Process in parallel"}
          />
          <div class="dialog_actions">
            <Button
              variant="secondary"
              text="Back"
              on:click={() => (stage = "next")}
            /><Button
              variant="secondary"
              text="Continue"
              disabled={!gatewayQuestion.trim()}
              on:click={confirmGatewayDetails}
            />
          </div>
        </div>
      {:else if stage === "branching"}
        <div class="branching_choices">
          <Button
            variant="secondary"
            text="Add as sequence"
            width="100%"
            on:click={() => addActivities(pendingTasks, "sequence")}
          /><Button
            variant="secondary"
            text="Add as parallel paths"
            width="100%"
            on:click={() => addActivities(pendingTasks, "parallel")}
          /><Button
            variant="secondary"
            text="Add as conditional paths"
            width="100%"
            on:click={() => prepareConditions(pendingTasks)}
          />
        </div>
      {:else if stage === "conditions"}
        <div class="condition_editor">
          <label
            ><span>XOR decision question</span><input
              type="text"
              bind:value={gatewayQuestion}
            /></label
          >{#each pendingTasks as task, index}<label
              ><span>Path to {task.name}</span><input
                type="text"
                bind:value={pathDescriptions[index]}
              /></label
            >{/each}<button
            class="primary full"
            type="button"
            disabled={!gatewayQuestion.trim() ||
              pathDescriptions.some((text) => !text.trim())}
            on:click={confirmConditions}>Create XOR paths</button
          >
        </div>
      {:else if stage === "information"}
        <div class="detail_section">
          <span class="detail_label">Export format</span>
          <div class="segmented">
            <button
              type="button"
              class:active={exportFormat === "standard"}
              on:click={() => chooseOutput("standard", orientation)}
              >Standard BPMN 2.0</button
            >
            <button
              type="button"
              class:active={exportFormat === "bic"}
              on:click={() => chooseOutput("bic", orientation)}
              >BIC/GBTEC</button
            >
          </div>
          {#if exportFormat === "bic"}
            <label class="detail_check">
              <input
                type="checkbox"
                bind:checked={generateTexts}
                on:change={() =>
                  dispatch("output", {
                    exportFormat,
                    orientation,
                    generateTexts,
                  })}
              />
              <span>Generate descriptions and keywords</span>
            </label>
          {/if}
        </div>
        <div class="detail_section">
          <span class="detail_label">Orientation</span>
          <div class="segmented">
            <button
              type="button"
              class:active={orientation === "horizontal"}
              on:click={() => chooseOutput(exportFormat, "horizontal")}
              >Horizontal</button
            >
            <button
              type="button"
              class:active={orientation === "vertical"}
              on:click={() => chooseOutput(exportFormat, "vertical")}
              >Vertical</button
            >
          </div>
        </div>
        <div class="detail_section">
          <span class="detail_label">Additional information</span>
          <div class="information_grid">
            <label
              ><input type="checkbox" bind:checked={information.avgTime} />Avg.
              activity time</label
            ><label
              ><input
                type="checkbox"
                bind:checked={information.throughputTime}
              />Avg. throughput time</label
            ><label
              ><input
                type="checkbox"
                bind:checked={information.frequency}
              />Frequency</label
            ><label
              ><input type="checkbox" bind:checked={information.databases} />IT
              systems</label
            ><label
              ><input
                type="checkbox"
                bind:checked={information.files}
              />Files</label
            ><label
              ><input
                type="checkbox"
                bind:checked={information.addSubprocess}
              />Add subprocess</label
            >
          </div>
        </div>
        {#if information.databases || information.files}
          <div class="detail_section">
            <span class="detail_label">Minimum usage frequency</span>
            <div class="segmented">
              {#each ["simple", "balanced", "custom"] as mode}<button
                  type="button"
                  class:active={artifactMode === mode}
                  on:click={() => (artifactMode = mode)}
                  >{mode[0].toUpperCase() + mode.slice(1)}</button
                >{/each}
            </div>
            {#if artifactMode === "custom"}<div class="threshold_grid">
                <div class:disabled={!information.databases}>
                  <button
                    type="button"
                    disabled={!information.databases || systemMinFrequency <= 0}
                    on:click={() =>
                      (systemMinFrequency = Math.max(
                        0,
                        systemMinFrequency - 5,
                      ))}>−</button
                  ><span>Systems <strong>{effectiveSystemMin}%</strong></span
                  ><button
                    type="button"
                    disabled={!information.databases ||
                      systemMinFrequency >= 100}
                    on:click={() =>
                      (systemMinFrequency = Math.min(
                        100,
                        systemMinFrequency + 5,
                      ))}>+</button
                  >
                </div>
                <div class:disabled={!information.files}>
                  <button
                    type="button"
                    disabled={!information.files || fileMinFrequency <= 0}
                    on:click={() =>
                      (fileMinFrequency = Math.max(0, fileMinFrequency - 5))}
                    >−</button
                  ><span>Files <strong>{effectiveFileMin}%</strong></span
                  ><button
                    type="button"
                    disabled={!information.files || fileMinFrequency >= 100}
                    on:click={() =>
                      (fileMinFrequency = Math.min(100, fileMinFrequency + 5))}
                    >+</button
                  >
                </div>
              </div>{/if}
          </div>
        {/if}
        <Button
          variant="primary"
          text="Apply and complete"
          width="100%"
          on:click={applyInformation}
        />
      {:else if stage === "recordType"}
        <div class="record_types">
          <p>Select the object type for average throughput times:</p>
          {#each recordTypes as recordType}<button
              type="button"
              on:click={() => applyInformation(recordType)}
              >{recordType.name}</button
            >{/each}
        </div>
      {:else}
        <div class="complete">
          <div>
            <strong>Model complete</strong><span
              >{diagramState.activities?.length || 0} activities · {diagramState.gateways ||
                0} gateways</span
            >
          </div>
          <Button
            variant="secondary"
            text="Continue modeling"
            width="100%"
            on:click={() => (stage = "next")}
          /><Button
            variant="secondary"
            text="Save Model"
            width="100%"
            on:click={() => dispatch("save")}
          />
        </div>
      {/if}
    </div>
  </div>

  <DangerModal
    danger
    size="sm"
    class="bpmn_reset_danger_modal"
    bind:open={resetDangerModalOpen}
    modalHeading="Reset current changes?"
    primaryButtonText="Reset changes"
    preventCloseOnClickOutside
    secondaryButtonText="Cancel"
    on:click:button--primary={() => {
      resetDangerModalOpen = false;
      reset();
    }}
    on:click:button--secondary={() => (resetDangerModalOpen = false)}
    on:close={() => (resetDangerModalOpen = false)}
  >
    <p class="reset_modal_body">
      Are you sure you want to reset the current changes?
      <span>This action cannot be undone.</span>
    </p>
  </DangerModal>

  {#if manualModalOpen}<div
      class="manual_modal_backdrop"
      role="presentation"
      on:click|self={() => (manualModalOpen = false)}
    >
      <section
        class="manual_modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-activity-title"
      >
        <header>
          <div>
            <strong id="manual-activity-title">New manual activity</strong><span
              >Saved for this project and available in future BPMN exports.</span
            >
          </div>
          <button
            type="button"
            aria-label="Close"
            on:click={() => (manualModalOpen = false)}>×</button
          >
        </header>
        <div class="manual_fields">
          <label class="wide"
            ><span>Activity name</span><input
              bind:value={manualForm.name}
              placeholder="Enter activity name"
            /></label
          ><label
            ><span>Team</span><select bind:value={manualForm.team}
              ><option value="Other Teams">Other Teams</option
              >{#each teamOptions as team}<option value={team}>{team}</option
                >{/each}</select
            ></label
          ><label
            ><span>Average time</span>
            <div class="duration_inputs">
              <input
                type="number"
                min="0"
                bind:value={manualForm.avgMinutes}
                aria-label="Average time minutes"
              /><em>min</em><i>|</i><input
                type="number"
                min="0"
                max="59"
                bind:value={manualForm.avgSeconds}
                aria-label="Average time seconds"
              /><em>sec</em>
            </div></label
          ><label
            ><span>Frequency / month</span><input
              type="number"
              min="0"
              bind:value={manualForm.frequency}
            /></label
          ><label class="wide"
            ><span>IT systems <small>Comma-separated</small></span><input
              bind:value={manualForm.systems}
              placeholder="e.g. SAP, SharePoint"
            /></label
          ><label class="wide"
            ><span>Files <small>Comma-separated</small></span><input
              bind:value={manualForm.files}
              placeholder="e.g. Invoice.xlsx, Request.pdf"
            /></label
          >{#if manualError}<p class="manual_error">{manualError}</p>{/if}
        </div>
        <footer>
          <button type="button" on:click={() => (manualModalOpen = false)}
            >Cancel</button
          ><button
            class="primary"
            type="button"
            disabled={!manualForm.name.trim() || manualSaving}
            on:click={saveManualActivity}
            >{manualSaving ? "Saving..." : "Save activity"}</button
          >
        </footer>
      </section>
    </div>{/if}
</section>

<style>
  .draw_assistant {
    height: min(72vh, 52rem);
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    overflow: hidden;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background: #f7f8fa;
  }
  header {
    min-height: 3.6rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.8rem;
    background: var(--pax-orange);
    color: #fff;
  }
  header > div:first-child {
    display: flex;
    flex-direction: column;
  }
  header span {
    font-size: 0.68rem;
  }
  .header_actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }
  .header_actions button {
    width: 2rem;
    height: 2rem;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(255, 255, 255, 0.7);
    border-radius: 0.35rem;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    cursor: pointer;
  }
  .header_actions button img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }
  .assistant_scroll {
    min-height: 0;
    overflow-y: auto;
    padding: 0.7rem;
  }
  .moderator_card {
    display: grid;
    grid-template-columns: 3rem 1fr;
    gap: 0.7rem;
    align-items: center;
    margin-bottom: 0.7rem;
    padding: 0.7rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.5rem;
    background: #fff;
  }
  .moderator_card img {
    width: 3rem;
    height: 3rem;
    object-fit: contain;
  }
  .moderator_card strong {
    font-size: 0.68rem;
    color: var(--pax-orange);
  }
  .moderator_card p {
    margin: 0.25rem 0 0;
    font-size: 0.74rem;
    line-height: 1.4;
  }
  .suggestions {
    padding: 0.75rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.5rem;
    background: #fff;
  }
  .start_choices {
    display: grid;
    gap: 0.5rem;
  }
  .drawing_search {
    box-sizing: border-box;
    width: 100%;
    height: 2.35rem;
    margin-bottom: 0.55rem;
    padding: 0 0.7rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
  }
  .framework_switcher {
    margin-bottom: 0.6rem;
  }

  .suggestion_list {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .suggestion_list button {
    min-height: 2rem;
    padding: 0.35rem 0.6rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
    background: #fff;
    text-align: left;
    cursor: pointer;
  }
  .suggestion_list button:hover {
    border-color: var(--pax-orange);
  }
  .framework_list {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
  .suggestion_list .framework_option {
    height: 2.125rem;
    min-height: 2.125rem;
    overflow: hidden;
    padding: 0 1rem;
    border-color: var(--pax-smoke);
    border-radius: 0.5rem;
    background: var(--pax-smoke);
    color: var(--pax-black);
    font-size: 0.8125rem;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .suggestion_list .framework_option:hover {
    border-color: var(--pax-black);
  }
  .pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    margin-top: 0.5rem;
    font-size: 0.7rem;
  }
  .pagination button {
    border: 0;
    background: transparent;
    cursor: pointer;
  }
  .task_tools {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 8.5rem 2rem;
    gap: 0.4rem;
    margin-bottom: 0.5rem;
  }
  .task_tools input,
  .task_tools select {
    min-width: 0;
    height: 2rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
    background: #fff;
  }
  .task_tools button {
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 1px solid var(--pax-orange);
    border-radius: 0.35rem;
    background: var(--pax-orange);
    color: #fff;
    font-size: 1.15rem;
    cursor: pointer;
  }
  .activity_table {
    max-height: 12.5rem;
    overflow-y: auto;
    font-size: 0.72rem;
  }
  .activity_table_header,
  .activity_table_row {
    display: grid;
    grid-template-columns: 4.25rem minmax(0, 1fr) 6.5rem;
    align-items: center;
    gap: 0.45rem;
  }
  .activity_table_header {
    min-height: 1.8rem;
    padding: 0 0.55rem;
    border-radius: 0.4rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-weight: 700;
  }
  .activity_table_row {
    min-height: 2rem;
    padding: 0 0.55rem;
    border-bottom: 1px solid #d9d9d9;
    cursor: pointer;
  }
  .activity_table_row input {
    width: 1rem !important;
    height: 1rem;
    margin: 0;
    accent-color: var(--pax-black);
  }
  .activity_table_row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .activity_table_empty {
    padding: 1rem 0.55rem;
    color: var(--pax-grey);
    text-align: center;
  }
  .saved_model_table {
    max-height: 12.5rem;
    overflow-y: auto;
    font-size: 0.72rem;
  }
  .saved_model_table_header,
  .saved_model_table_row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 5.5rem;
    align-items: center;
    gap: 0.45rem;
  }
  .saved_model_table_header {
    min-height: 1.8rem;
    padding: 0 0.55rem;
    border-radius: 0.4rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-weight: 700;
  }
  .saved_model_table_row {
    width: 100%;
    min-height: 2rem;
    padding: 0 0.55rem;
    border: 0;
    border-bottom: 1px solid #d9d9d9;
    background: transparent;
    color: var(--pax-black);
    text-align: left;
    cursor: pointer;
  }
  .saved_model_table_row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .saved_model_table_row span:last-child {
    text-align: right;
  }
  .saved_model_table_empty {
    padding: 1rem 0.55rem;
    color: var(--pax-grey);
    text-align: center;
  }
  .manual_badge {
    display: inline-block;
    margin-left: 0.4rem;
    padding: 0.08rem 0.3rem;
    border-radius: 1rem;
    background: #fff0e9;
    color: var(--pax-orange);
    font-size: 0.56rem;
    font-weight: 700;
  }
  /* Zwei Zeilen statt vier Spalten: in der schmalen Assistentenspalte
     quetschten die Knoepfe sonst die Blaetterung zusammen. */
  .task_footer {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0.5rem 0.45rem;
    min-height: 2.65rem;
    margin-top: 0.35rem;
  }
  .task_footer .pagination {
    justify-content: flex-start;
    margin-top: 0;
  }
  .selection_count {
    color: #6f7780;
    font-size: 0.67rem;
    text-align: right;
  }
  .task_footer_actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    gap: 0.45rem;
  }
  .task_footer > button {
    white-space: nowrap;
  }
  .primary {
    border-color: var(--pax-orange) !important;
    background: var(--pax-orange) !important;
    color: #fff;
  }
  .primary:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .task_footer > button,
  .full {
    min-height: 2rem;
    padding: 0 0.7rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
    background: #fff;
    cursor: pointer;
    font-weight: 700;
  }
  .next_choices,
  .information_grid {
    display: grid;
    gap: 0.45rem;
  }
  .information_grid {
    grid-template-columns: 1fr 1fr;
  }
  .information_grid label {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.35rem;
    font-size: 0.74rem;
  }
  .information_grid input {
    width: 1rem !important;
  }
  .full {
    width: 100%;
    margin-top: 0.55rem;
  }
  .complete {
    display: grid;
    gap: 0.65rem;
    font-size: 0.72rem;
  }
  .complete > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }
  .branching_choices {
    display: grid;
    gap: 0.5rem;
  }
  .condition_editor {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }
  .condition_editor label {
    display: grid;
    gap: 0.2rem;
    font-size: 0.7rem;
  }
  .condition_editor input {
    height: 2rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
  }
  .dialog_actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.45rem;
  }
  .record_types {
    display: grid;
    gap: 0.4rem;
  }
  .record_types p {
    margin: 0 0 0.2rem;
    font-size: 0.74rem;
  }
  .record_types button {
    min-height: 2rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
    background: #fff;
    text-align: left;
  }
  /* Abschnitte der Details-Phase: gleiche Anmutung wie der BPMN-Export im
     Process Explorer - Ueberschrift, darunter ein segmentierter Schalter. */
  .detail_section {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 0.85rem;
  }

  .detail_label {
    font-size: 0.75rem;
    font-weight: 700;
  }

  .segmented {
    display: flex;
    width: 100%;
    overflow: hidden;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
  }

  .segmented button {
    flex: 1;
    height: 2.35rem;
    border: 0;
    border-right: 1px solid #d9d9d9;
    background: var(--pax-white);
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
  }

  .segmented button:last-child {
    border-right: 0;
  }

  .detail_check {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.5rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.35rem;
    font-size: 0.74rem;
    cursor: pointer;
  }

  .detail_check input {
    width: 1rem !important;
  }

  .segmented button.active {
    background: var(--pax-black);
    color: var(--pax-white);
  }

  .threshold_grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.4rem;
  }
  .threshold_grid > div {
    display: grid;
    grid-template-columns: 2rem 1fr 2rem;
    align-items: center;
    border: 1px solid #e1e4e8;
    border-radius: 0.35rem;
  }
  .threshold_grid > div.disabled {
    opacity: 0.45;
  }
  .threshold_grid button {
    height: 2rem;
    border: 0;
    background: #f4f4f4;
  }
  .threshold_grid span {
    text-align: center;
    font-size: 0.68rem;
  }
  :global(.BPMN-modal-wrapper .bpmn_reset_danger_modal) {
    position: fixed !important;
    inset: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 10001 !important;
  }
  :global(.BPMN-modal-wrapper .bpmn_reset_danger_modal .bx--modal-container) {
    position: static !important;
    width: min(92vw, 30rem) !important;
    max-width: 30rem !important;
    margin: 0 !important;
    transform: none !important;
  }
  .reset_modal_body {
    padding: 23px 0;
    font-size: 14px;
    line-height: 1.5;
  }
  .reset_modal_body span {
    display: block;
    margin-top: 0.5rem;
    font-weight: 700;
  }
  .manual_modal_backdrop {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: grid;
    place-items: center;
    padding: 1rem;
    background: rgba(28, 28, 32, 0.45);
  }
  .manual_modal {
    width: min(32rem, 100%);
    overflow: hidden;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.24);
  }
  .manual_modal header {
    min-height: auto;
    padding: 0.8rem 1rem;
    background: #fff;
    color: #1c1c20;
    border-bottom: 1px solid #e1e4e8;
  }
  .manual_modal header span {
    margin-top: 0.15rem;
    color: #6f7780;
    font-size: 0.65rem;
  }
  .manual_modal header button {
    width: 2rem;
    height: 2rem;
    border: 0;
    background: transparent;
    font-size: 1.2rem;
  }
  .manual_fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.7rem;
    padding: 1rem;
  }
  .manual_fields label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.7rem;
    font-weight: 600;
  }
  .manual_fields .wide,
  .manual_error {
    grid-column: 1 / -1;
  }
  .manual_fields input,
  .manual_fields select {
    min-width: 0;
    width: 100%;
    height: 2.2rem;
    padding: 0 0.55rem;
    border: 1px solid #8d8d8d;
    border-radius: 0.35rem;
    background: #fff;
  }
  .duration_inputs {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.3rem;
  }
  .duration_inputs em,
  .duration_inputs i {
    color: #6f7780;
    font-size: 0.65rem;
    font-style: normal;
    font-weight: 400;
  }
  .manual_fields small {
    color: #8a929b;
    font-size: 0.58rem;
    font-weight: 400;
  }
  .manual_error {
    margin: 0;
    color: #b42318;
    font-size: 0.68rem;
  }
  .manual_modal footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #e1e4e8;
  }
  .manual_modal footer button {
    min-height: 2.2rem;
    padding: 0 0.8rem;
    border: 1px solid #d9d9d9;
    border-radius: 0.35rem;
    background: #fff;
    font-weight: 700;
  }
</style>
